import { HeaderPage } from "@/components/HeaderPage";
import { Loading } from "@/components/Loading";
import { Card, CardContent as CardContentUI } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useGetUserById } from "@/hooks/useUsers";
import { motion } from "framer-motion";
import { useParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";

const roleConfig: Record<
  string,
  { label: string; variant: "default" | "secondary" | "outline" }
> = {
  ADMIN: { label: "Administrador", variant: "default" },
  AGENT: { label: "Agente", variant: "secondary" },
  CITIZEN: { label: "Cidadão", variant: "outline" },
};

export const ViewUser = () => {
  const { id } = useParams<{ id: string }>();
  const { data: user, isLoading } = useGetUserById(id!);

  if (isLoading) {
    return <Loading label="Carregando usuário..." />;
  }

  if (!user) {
    return (
      <div>
        <HeaderPage
          title="Visualizar usuário"
          description="Informações detalhadas do usuário selecionado"
        />
        <div className="flex-1 overflow-y-auto mt-10">
          <Card className="bg-white">
            <CardContentUI className="p-6">
              <p className="text-center text-gray-500">
                Usuário não encontrado
              </p>
            </CardContentUI>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div>
      <HeaderPage
        title="Visualizar usuário"
        description="Informações detalhadas do usuário selecionado"
      />
      <div className="flex-1 overflow-y-auto mt-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card className="bg-white">
            <CardContentUI className="p-6">
              <form className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-4">
                    Informações da Conta
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input title="Email" disabled value={user.email} />

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Função
                      </label>
                      <Badge
                        variant={roleConfig[user.role]?.variant || "outline"}
                      >
                        {roleConfig[user.role]?.label || user.role}
                      </Badge>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Status
                      </label>
                      <Badge
                        variant={user.isActive ? "success" : "destructive"}
                      >
                        {user.isActive ? "Ativo" : "Inativo"}
                      </Badge>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-4">
                    Informações Pessoais
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      title="Nome Completo"
                      disabled
                      value={user.profile?.fullName || "-"}
                    />

                    <Input
                      title="CPF"
                      disabled
                      value={
                        user.profile?.cpf
                          ? user.profile.cpf
                              .replace(/\D/g, "")
                              .replace(
                                /(\d{3})(\d{3})(\d{3})(\d{2})/,
                                "$1.$2.$3-$4",
                              )
                          : "-"
                      }
                    />

                    <Input
                      title="Telefone"
                      disabled
                      value={
                        user.profile?.phone
                          ? user.profile.phone
                              .replace(/\D/g, "")
                              .replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3")
                          : "-"
                      }
                    />
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-4">Endereço</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      title="Rua"
                      disabled
                      value={user.profile?.street || "-"}
                    />

                    <Input
                      title="Número"
                      disabled
                      value={user.profile?.number || "-"}
                    />

                    <Input
                      title="Bairro"
                      disabled
                      value={user.profile?.neighborhood || "-"}
                    />

                    <Input
                      title="Cidade"
                      disabled
                      value={user.profile?.city || "-"}
                    />

                    <Input
                      title="Estado"
                      disabled
                      value={user.profile?.state || "-"}
                    />

                    <Input
                      title="CEP"
                      disabled
                      value={
                        user.profile?.zipCode
                          ? user.profile.zipCode.replace(
                              /^(\d{5})(\d{3})$/,
                              "$1-$2",
                            )
                          : "-"
                      }
                    />
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-4">
                    Informações do Sistema
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      title="Data de Cadastro"
                      disabled
                      value={new Date(user.dateJoined).toLocaleDateString(
                        "pt-BR",
                      )}
                    />

                    <Input
                      title="Último Login"
                      disabled
                      value={
                        user.lastLogin
                          ? new Date(user.lastLogin).toLocaleDateString("pt-BR")
                          : "-"
                      }
                    />
                  </div>
                </div>
              </form>
            </CardContentUI>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};
